/**
 * Approvals Page — every pending request the caller can decide, in one place,
 * grouped by what the request is about: Documents and Units. One feed behind
 * both sections; the grouping is a rendering choice, not two backends. Reached
 * from the Home header bell, whose badge carries the same pending count.
 *
 * The Knowledge Base keeps a contextual Approvals tab for the DOCUMENT subset;
 * this page is the cross-resource inbox.
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ApprovalRequestList,
  isDocumentAccessRequest,
  useApprovalsInbox,
} from '@/components/approvals/ApprovalsInbox';

export default function Approvals() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const inbox = useApprovalsInbox();

  const documentRequests = inbox.requests.filter(isDocumentAccessRequest);
  const unitRequests = inbox.requests.filter((r) => !isDocumentAccessRequest(r));

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="sticky top-0 z-10 bg-white border-b border-neutral-200">
        <div className="px-4 h-14 flex items-center gap-3">
          <button
            onClick={() => navigate('/home')}
            aria-label={t('approvals.backHome', 'Back to home')}
            className="w-9 h-9 -ml-2 flex items-center justify-center text-primary"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-semibold text-neutral-900">
            {t('approvals.title', 'Approvals')}
          </h1>
        </div>
      </header>

      <main className="p-4 max-w-3xl mx-auto space-y-4">
        {inbox.isError ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center justify-between gap-3">
            <span>{t('documents.approvals.loadError', 'Could not load pending approvals.')}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => inbox.refetch()}
              disabled={inbox.isFetching}
              className="shrink-0"
            >
              {inbox.isFetching ? t('common.loading') : t('actions.retry', 'Retry')}
            </Button>
          </div>
        ) : inbox.isPending ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : inbox.count === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
            <p className="text-neutral-600">{t('approvals.empty', 'No pending approvals.')}</p>
          </div>
        ) : (
          <>
            {inbox.hasMore && (
              <p className="text-sm text-amber-700">
                {t('documents.approvals.hasMore', 'More pending than shown — decide these to load the rest.')}
              </p>
            )}
            {documentRequests.length > 0 && (
              <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                <h2 className="px-4 py-3 text-sm font-semibold text-neutral-900 bg-neutral-50 border-b border-neutral-200">
                  {t('approvals.sections.documents', 'Documents')}
                </h2>
                <ApprovalRequestList requests={documentRequests} />
              </section>
            )}
            {unitRequests.length > 0 && (
              <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                <h2 className="px-4 py-3 text-sm font-semibold text-neutral-900 bg-neutral-50 border-b border-neutral-200">
                  {t('approvals.sections.units', 'Units')}
                </h2>
                <ApprovalRequestList requests={unitRequests} />
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
